import { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText, Loader2, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../guias/guiasUtils';

const ETAPAS = ['Preparando fechamento documental...', 'Organizando documentos...', 'Gerando PDF consolidado...', 'PDF pronto para download.'];

const salvarPdf = (data, nome) => {
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const a = document.createElement('a'); a.href = url; a.download = nome; a.click(); URL.revokeObjectURL(url);
};

const lerErro = async (e) => {
  const blob = e?.response?.data;
  if (blob instanceof Blob) { try { return JSON.parse(await blob.text()); } catch { return null; } }
  return e?.response?.data || null;
};

export const PdfConsolidadoDialog = ({ open, onOpenChange, api, pacienteId, detalhe, params, especialidadeLabel, onDone }) => {
  const [etapa, setEtapa] = useState(-1);
  const [problemas, setProblemas] = useState(null);
  const busy = etapa >= 0 && etapa < 3;
  const d = detalhe;
  const guias = especialidadeLabel ? (d?.guias_por_especialidade?.find((g) => g.label === especialidadeLabel)?.total ?? 0) : d?.contagens?.guia ?? 0;
  const complementares = especialidadeLabel ? 0 : (d?.total_documentos ?? 0) - (d?.contagens?.guia ?? 0);
  const total = guias + complementares;
  const intSel = d?.internacoes?.find((i) => i.id === params?.internacao_id);

  const gerar = async (parcial = false) => {
    setEtapa(0); setProblemas(null);
    const t1 = setTimeout(() => setEtapa(1), 700); const t2 = setTimeout(() => setEtapa(2), 1800);
    try {
      const r = await api.get(`/fechamento/competencias/${pacienteId}/${d.competencia}/download`, { params: { ...params, ...(parcial ? { parcial: true } : {}) }, responseType: 'blob', timeout: 300000 });
      clearTimeout(t1); clearTimeout(t2); setEtapa(3);
      salvarPdf(r.data, r.headers['x-arquivo-nome'] || `FECHAMENTO_${d.competencia}.pdf`);
      toast.success(`PDF consolidado gerado: ${r.headers['x-arquivos']} documentos, ${r.headers['x-paginas']} páginas${parcial ? ' (INCOMPLETO)' : ''}`);
      onDone?.();
      setTimeout(() => { onOpenChange(false); setEtapa(-1); }, 900);
    } catch (e) {
      clearTimeout(t1); clearTimeout(t2); setEtapa(-1);
      const det = (await lerErro(e))?.detail;
      if (e?.response?.status === 422 && det?.problemas) { setProblemas(det); return; }
      toast.error(typeof det === 'string' ? det : errMsg(e, 'Erro ao gerar PDF consolidado'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) { setEtapa(-1); setProblemas(null); } } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pdf-consolidado-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />PDF consolidado{especialidadeLabel ? ` — ${especialidadeLabel}` : ''}</DialogTitle>
          <DialogDescription>Um único PDF com capa, resumo, índice e todos os documentos originais incorporados, na seleção atual da tela.</DialogDescription>
        </DialogHeader>
        {d && (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1" data-testid="pdf-consolidado-resumo">
            <p><span className="text-muted-foreground">Paciente:</span> <strong>{d.paciente?.nome}</strong></p>
            <p><span className="text-muted-foreground">Internação:</span> {intSel ? `Prontuário ${intSel.numero_prontuario || '—'}` : 'Histórico consolidado (todas)'}</p>
            <p><span className="text-muted-foreground">Período:</span> {d.periodo?.inicio?.split('-').reverse().join('/')} a {d.periodo?.fim?.split('-').reverse().join('/')}</p>
            <p><span className="text-muted-foreground">Documentos selecionados:</span> <strong data-testid="pdf-consolidado-total">{total}</strong> ({guias} guias{especialidadeLabel ? '' : `, ${complementares} complementares`})</p>
          </div>
        )}
        {etapa >= 0 && (
          <div className="space-y-1" data-testid="pdf-consolidado-etapas">
            {ETAPAS.map((t, i) => (
              <div key={t} className={`flex items-center gap-2 text-sm ${i > etapa ? 'text-muted-foreground/50' : i === etapa && etapa < 3 ? 'text-blue-700' : 'text-emerald-700'}`}>
                {i < etapa || etapa === 3 ? <CheckCircle2 className="h-4 w-4" /> : i === etapa ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="h-4 w-4 inline-block rounded-full border" />}
                <span data-testid={`pdf-etapa-${i}`}>{t}</span>
              </div>
            ))}
          </div>
        )}
        {problemas && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm space-y-2" data-testid="pdf-consolidado-problemas">
            <p className="font-medium text-rose-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{problemas.mensagem}</p>
            <ul className="list-disc pl-5 text-rose-900 text-xs space-y-0.5">
              {problemas.problemas.map((p) => <li key={p.id} data-testid={`pdf-problema-${p.id}`}>{p.secao} · {p.data || '—'} · {p.nome || p.id} — {p.motivo}</li>)}
            </ul>
            <p className="text-xs text-rose-800">Documentos legíveis: {problemas.validos}. O status administrativo da competência não foi alterado.</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="pdf-consolidado-cancelar">Cancelar</Button>
          {problemas
            ? <Button variant="destructive" onClick={() => gerar(true)} disabled={busy || !problemas.validos} data-testid="pdf-consolidado-incompleto"><Download className="h-4 w-4 mr-2" />Baixar PDF INCOMPLETO</Button>
            : <Button onClick={() => gerar(false)} disabled={busy || total === 0} data-testid="pdf-consolidado-gerar">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Gerar PDF ({total} documentos)</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
